import { h, Component } from 'preact';
import { observer } from 'mobx-preact';
import { Text } from 'preact-i18n';
import md5 from 'crypto-js/md5';

import DragNDropFileupload from '../form/dragNdropFileupload';

/**
 * Dead-simple profile photo upload drawer. Opened with:
 *   appState.openDrawer('uploadUserImage', { userId, name, callback }, level)
 *
 * The file picker opens automatically (autoOpen) so on a phone the camera /
 * gallery sheet is one tap away. Saves onto user.images the same way the
 * profile edit form does (saveField for yourself, adminUpdate for admins),
 * shows the saved photo, then closes itself.
 */
@observer
class UploadUserImage extends Component {
    constructor(props) {
        super(props);
        this.state = {
            savedImage: null,
            saving: false,
            error: '',
        };
        this.closeTimer = null;
    }

    componentWillUnmount() {
        if (this.closeTimer) clearTimeout(this.closeTimer);
    }

    handleAddImage = async (file) => {
        const { userId, callback = () => {}, drawerLevel } = this.props;
        const { userStore, appState } = this.props.stores;
        if (!file || file._pendingUpload || !userId) return;

        const imageObject = {
            ...file,
            md5: md5(JSON.stringify(file)).toString(),
            date: new Date(),
        };

        this.setState({ saving: true, error: '' });
        const { user = {}, isAdmin } = userStore;
        let response;
        if (user.id === userId) {
            // Same path as the profile edit form: PATCH { images: [img] } on yourself.
            response = await userStore.saveField(userId, 'images', [imageObject]);
        } else if (isAdmin) {
            response = await userStore.adminUpdate(userId, { images: [imageObject] });
        } else {
            this.setState({
                saving: false,
                error: 'Du kan bare laste opp bilde for deg selv.',
            });
            return;
        }
        // saveField always returns the response; adminUpdate returns undefined
        // on anything but 202 — treat both shapes as failure.
        if (!response || response.status >= 400) {
            this.setState({ saving: false, error: (response && response.message) || 'Kunne ikke lagre bildet' });
            return;
        }

        this.setState({ saving: false, savedImage: imageObject });
        callback(imageObject);
        appState.addToast({ type: 'success', message: 'Bildet er lagret!' }, 3000);
        // Let the confirmation sink in, then close the drawer by itself.
        this.closeTimer = setTimeout(() => {
            appState.toggleDrawer(false, drawerLevel);
        }, 1800);
    };

    render() {
        const { name } = this.props;
        const { savedImage, saving, error } = this.state;
        return (
            <div class='p-4 d-flex flex-column align-items-center text-center w-100' style='max-width: 480px; margin: 0 auto;'>
                <h4 class='mb-1'>
                    <i class='fa-duotone fa-camera-retro me-2 text-primary' />
                    <Text id='user.upload-image-title'>Upload profile photo</Text>
                </h4>
                {name && <div class='text-body-secondary mb-3'>{name}</div>}

                {error && (
                    <div class='alert alert-danger py-2 w-100'>{error}</div>
                )}

                {savedImage ? (
                    <div class='d-flex flex-column align-items-center gap-3 mt-2'>
                        <img
                            src={savedImage.s3MediumLink}
                            alt={name || ''}
                            class='rounded-circle bg-body-secondary'
                            style='width: 160px; height: 160px; object-fit: cover;'
                        />
                        <div class='text-success fw-bold fs-5'>
                            <i class='fa-solid fa-circle-check me-2' />
                            <Text id='user.upload-image-success'>Photo saved!</Text>
                        </div>
                    </div>
                ) : (<>
                    <div class='text-body-secondary mb-3'>
                        <Text id='user.upload-image-help'>Take a photo or choose one from your gallery.</Text>
                    </div>
                    <div class='w-100'>
                        <DragNDropFileupload
                            handleAddImage={this.handleAddImage}
                            apiUrl='/api/fileupload'
                            stores={this.props.stores}
                            accept='image/*'
                            imagesOnly={true}
                            autoOpen={true}
                            field='userProfileImage'
                            allowOfflineStash={false}
                        />
                    </div>
                    {saving && (
                        <div class='mt-3 text-body-secondary'>
                            <i class='fa-duotone fa-spinner-third fa-spin me-2' />
                            <Text id='user.upload-image-saving'>Saving…</Text>
                        </div>
                    )}
                </>)}
            </div>
        );
    }
}

export default UploadUserImage;
